// Best AI recruiting software for agencies (/guides/best-ai-recruiting-software).
// Evidence policy: every competitor fact comes from that vendor's own website (or, for a
// Bullhorn integration, its Bullhorn Marketplace listing), read on AI_RECRUITING_REVIEWED and
// listed in `sources`. "Not stated" means the vendor's pages did not say. Beatview's facts
// match /pricing and the alternatives data.

import type { VendorReview } from '@/components/page/VendorReviews';

export const AI_RECRUITING_REVIEWED = '2026-09-30';
export const AI_RECRUITING_REVIEWED_LABEL = '30 September 2026';

export type StageId = 'screening' | 'sourcing' | 'notes' | 'ats';

export const STAGES: { id: StageId; title: string; short: string; body: string }[] = [
  { id: 'screening', short: 'Screening and interviews', title: 'Screen and interview inbound applicants', body: 'The AI reads every application against the role, runs a structured first interview by video, voice or chat, and ranks candidates with the evidence. This is where agencies with busy inbound roles save the most consultant hours.' },
  { id: 'sourcing', short: 'Sourcing', title: 'Find passive candidates', body: 'The AI searches profiles beyond your own database, builds longlists and drafts outreach. It helps on hard-to-fill and search roles, but it does not assess people who have already applied.' },
  { id: 'notes', short: 'Interview notes', title: 'Capture live interviews', body: 'The AI records or joins your calls and writes notes, summaries and feedback. It saves admin after a conversation, but it does not replace the first screen.' },
  { id: 'ats', short: 'Agency ATS with AI', title: 'Run the desk in an ATS with AI built in', body: 'Agency ATS and CRM platforms now include AI for matching, writing and screening. Start here if you are choosing a system of record anyway.' },
];

export type AiTool = VendorReview & {
  stage: StageId;
  /** One line for the comparison table. */
  aiDoes: string;
  agencyFit: string;
  atsFit: string;
  sources: { label: string; url: string }[];
};

export const AI_TOOLS: AiTool[] = [
  {
    id: 'beatview',
    name: 'Beatview',
    website: 'https://beatview.ai',
    stage: 'screening',
    category: 'AI screening and interviews for agencies',
    bestFor: 'Recruitment agencies that want every inbound application screened and the shortlist interviewed, with evidence they can share with clients',
    summary: 'Beatview scores each application against the criteria a consultant sets, invites qualified candidates to timed one-way AI video interviews, scores each answer with the reasoning visible, and shares candidates with clients through password-protected links. Recruiters make every decision.',
    strengths: [
      'A job-match score with reasons for every resume',
      'Structured one-way video interviews, scored answer by answer (Pro)',
      'Stage rules that invite qualified candidates to interview automatically',
      'A Work Style assessment across six dimensions (Pro)',
      'An MCP server for Claude, ChatGPT and Gemini, on every plan',
    ],
    watchOuts: [
      'AI video interviews and Work Style assessments need Pro',
      'One-way video only: no live video, voice or chat interviews',
      'Does not source passive candidates',
      'Zapier, API access and webhooks are on Enterprise',
    ],
    pricing: 'Free (one active job); Pro $219/month, or $179/month billed annually; Enterprise on request',
    details: [['Interview format', 'Timed one-way video'], ['Free option', 'Free plan: one active job, unlimited candidates']],
    aiDoes: 'Scores resumes with reasons; runs and scores one-way video interviews; ranks candidates',
    agencyFit: 'Built for agencies; password-protected client sharing',
    atsFit: 'Zapier on Enterprise; native Bullhorn, JobAdder, Vincere, Greenhouse or Workable integration on request',
    sources: [
      { label: 'Beatview pricing', url: 'https://beatview.ai/pricing' },
      { label: 'Beatview AI interviews', url: 'https://beatview.ai/ai-interviews' },
      { label: 'Beatview MCP server', url: 'https://beatview.ai/mcp' },
    ],
    isBeatview: true,
  },
  {
    id: 'alex',
    name: 'Alex',
    website: 'https://www.alex.com',
    stage: 'screening',
    category: 'AI recruiter: video, phone and chat interviews',
    bestFor: 'Staffing and RPO firms that want an AI recruiter to screen every applicant and interview by video, phone or chat, under each client’s brand',
    summary: 'Alex is an AI recruiter that screens resumes, finds past candidates in your ATS, runs two-way interviews by video, phone, SMS or WhatsApp, and schedules next steps before handing qualified candidates to a recruiter. For staffing and RPO firms it can present as each client, with that client’s scripts, questions and languages.',
    strengths: [
      'Two-way AI interviews by video, phone, SMS or WhatsApp',
      'Scores every resume and resurfaces past candidates from the ATS',
      'Per-client scripts, branding and languages for staffing and RPO',
      'Fraud, identity and knowledge checks during interviews',
      'Names 33+ officially supported ATS integrations, including Bullhorn and Loxo',
    ],
    watchOuts: [
      'Pricing is not published; the pricing page leads to a demo booking',
      'Its own pages give 26 to 30 supported languages; confirm yours',
      'Vincere is not named among its integrations',
      'Its SOC 2 report is available under NDA',
      'The operating company is Apriora Inc., trading as Alex',
    ],
    pricing: 'Not published (demo and quote)',
    details: [['Interview format', 'Two-way video, phone, SMS, WhatsApp'], ['Free option', 'Not stated']],
    aiDoes: 'Screens resumes; interviews by video, phone or chat; checks identity; schedules',
    agencyFit: 'Markets to staffing and RPO; per-client branding',
    atsFit: '33+ ATS named, including Bullhorn, Loxo, Recruit CRM and JobAdder',
    sources: [
      { label: 'Alex home page', url: 'https://www.alex.com/' },
      { label: 'Alex AI Interviewer', url: 'https://www.alex.com/product/ai-interviewer' },
      { label: 'Alex platform', url: 'https://www.alex.com/platform' },
      { label: 'Alex for RPO and staffing', url: 'https://www.alex.com/industries/recruitment-process-outsourcing' },
      { label: 'Alex integrations', url: 'https://www.alex.com/integrations' },
    ],
  },
  {
    id: 'heymilo',
    name: 'HeyMilo',
    website: 'https://www.heymilo.ai',
    stage: 'screening',
    category: 'AI recruiter: voice and video screening',
    bestFor: 'High-volume staffing desks that want every applicant screened by AI phone or video interview, with results written back to Bullhorn',
    summary: 'HeyMilo screens and interviews every applicant through AI phone and two-way video interviews, SMS and forms, then sends transcript-backed scores and reports back to the ATS. Its Bullhorn integration writes reports to notes, fills in skills and creates job submissions.',
    strengths: [
      'AI phone interviews (by phone or in the browser) and scored two-way video interviews',
      'Resume auto-scoring, SMS screening and scenario-based skill checks',
      'Two-way sync with 20+ ATS, including Bullhorn, Recruit CRM, Zoho Recruit and Manatal',
      'Cheating detection, also sold on its own as HeyMilo Red',
      'White labelling, a public API and an MCP server',
    ],
    watchOuts: [
      'Pricing is not published; the pricing page leads to a demo booking',
      'Language counts differ by page: 20+ overall, 14+ for phone interviews',
      'JobAdder and Vincere are not named among its integrations',
      'Staffing rollouts start as pilots; pilot terms are not published',
    ],
    pricing: 'Not published (demo and quote)',
    details: [['Interview format', 'AI phone, two-way video, SMS, forms'], ['Free option', 'Not stated']],
    aiDoes: 'Scores resumes; runs AI phone and video interviews; flags cheating',
    agencyFit: 'Dedicated staffing agency solution',
    atsFit: '20+ ATS with two-way sync, including Bullhorn, Recruit CRM and Manatal',
    sources: [
      { label: 'HeyMilo home page', url: 'https://www.heymilo.ai/' },
      { label: 'HeyMilo candidate interviewing', url: 'https://www.heymilo.ai/interviewing-candidates' },
      { label: 'HeyMilo for staffing agencies', url: 'https://www.heymilo.ai/solutions/staffing-agencies' },
      { label: 'HeyMilo integrations', url: 'https://www.heymilo.ai/integrations' },
      { label: 'HeyMilo Bullhorn integration', url: 'https://www.heymilo.ai/integrations/bullhorn' },
    ],
  },
  {
    id: 'tenzo',
    name: 'Tenzo',
    website: 'https://www.tenzo.ai',
    stage: 'screening',
    category: 'AI recruiter: voice interviews',
    bestFor: 'Staffing agencies and RPOs that want every applicant contacted and interviewed by an AI recruiter within minutes, on top of an existing ATS',
    summary: 'Tenzo is an AI recruiter that sits on top of the ATS you already use. Its interviewer, Morgan, contacts applicants by phone, text, email or WhatsApp, interviews them against a rubric with knockout questions, and writes the score, transcript and recording back to the candidate record.',
    strengths: [
      'Contacts every applicant within minutes by phone, text, email or WhatsApp',
      'Rubric scoring with custom criteria, weighting and knockout questions',
      'Interviews in 40+ languages',
      'Fraud and identity checks using 40+ signals',
      'Names 57+ ATS and CRM integrations, including Bullhorn, Crelate and Tempworks',
    ],
    watchOuts: [
      'Pricing is by tailored quote only',
      'Video is listed on the home page but not in its channel lists; confirm it',
      'JobAdder and Vincere are not named; the full integration list is on request',
      'Not an ATS: it needs one underneath',
    ],
    pricing: 'Not published (tailored quote)',
    details: [['Interview format', 'Voice and phone, text, email, WhatsApp'], ['Free option', 'Free consultation; trial not stated']],
    aiDoes: 'Contacts and interviews applicants by voice; scores against a rubric; schedules',
    agencyFit: 'Dedicated staffing and RPO solution',
    atsFit: '57+ ATS and CRMs named, including Bullhorn, Crelate and JobDiva',
    sources: [
      { label: 'Tenzo home page', url: 'https://www.tenzo.ai/' },
      { label: 'Tenzo solutions', url: 'https://www.tenzo.ai/solutions' },
      { label: 'Tenzo for staffing agencies and RPO', url: 'https://www.tenzo.ai/solutions/staffing-agencies-rpo' },
      { label: 'Tenzo integrations', url: 'https://www.tenzo.ai/integrations' },
      { label: 'Tenzo pricing', url: 'https://www.tenzo.ai/pricing' },
    ],
  },
  {
    id: 'glider-ai',
    name: 'Glider AI',
    website: 'https://glider.ai',
    stage: 'screening',
    category: 'AI screening, interviews and skills checks',
    bestFor: 'Staffing firms on Bullhorn that want AI phone screens, video interviews and skills assessments, with proof of skills to send clients',
    summary: 'Glider AI, now owned by Findem, combines AI agents for outreach and chat screening, AI phone screens, autonomous AI video interviews, skills assessments and proctoring with ID checks. It sells to staffing firms directly, around presenting proof of skills to clients, and is listed on the Bullhorn Marketplace.',
    strengths: [
      'AI phone screens with scores, transcripts and summaries',
      'Autonomous AI video interviews with adaptive follow-up questions',
      'Skills, coding and behavioural assessments with AI proctoring and ID checks',
      'Chat screening over SMS, WhatsApp and the web',
      'Listed on the Bullhorn Marketplace; also integrates with Workday, Greenhouse and the Beeline VMS',
    ],
    watchOuts: [
      'Pricing is not published; the pricing page leads to a demo request',
      'Now part of Findem, following an acquisition announced on its home page',
      'Its demo page describes the platform as built for enterprise hiring teams',
      'JobAdder and Vincere are not named among its integrations',
    ],
    pricing: 'Not published (demo and quote)',
    details: [['Interview format', 'AI phone, AI video, chat, live coding'], ['Free option', 'Not stated']],
    aiDoes: 'AI phone screens and video interviews; skills tests; ID checks',
    agencyFit: 'Dedicated staffing firm solution',
    atsFit: 'Bullhorn Marketplace listing; Greenhouse, Workday, JobDiva, Ceipal',
    sources: [
      { label: 'Glider AI home page', url: 'https://glider.ai/' },
      { label: 'Glider AI Recruiter', url: 'https://glider.ai/product/ai-recruiter/' },
      { label: 'Glider AI Voice Recruiter', url: 'https://glider.ai/product/ai-voice-recruiter/' },
      { label: 'Glider AI video interviews', url: 'https://glider.ai/product/ai-interview-software/ai-video-interviews/' },
      { label: 'Glider AI for staffing firms', url: 'https://offers.glider.ai/solutions/staffing-firms' },
      { label: 'Glider AI on the Bullhorn Marketplace', url: 'https://www.bullhorn.com/marketplace/glider-ai/' },
    ],
  },
  {
    id: 'hirevue',
    pricingHref: '/guides/hirevue-pricing',
    name: 'HireVue',
    website: 'https://www.hirevue.com',
    stage: 'screening',
    category: 'Enterprise AI interviewing and assessments',
    bestFor: 'Enterprise employers, and RPO or staffing firms serving them, that want voice AI interviews, video interviewing and assessments from one vendor',
    summary: 'HireVue combines a voice-based AI Interviewer that scores answers against a role rubric and explains why, one-way and live video interviewing, skills and game-based assessments, and a careers-site chat agent. It has a solution page for RPO and staffing firms, built around their enterprise clients.',
    strengths: [
      'Voice AI Interviewer that asks follow-up questions and scores against a role rubric',
      'One-way and live video interviews in HireVue, Zoom, Teams or Webex',
      'Skills tests, game-based assessments and an Assessment Builder',
      'Integrations with 45+ ATS, including Workday, SAP SuccessFactors and Greenhouse',
      'Candidates can opt out of the AI interview and take an equivalent non-AI path',
    ],
    watchOuts: [
      'Pricing is not published; HireVue says it serves mid-market to enterprise',
      'Bullhorn, JobAdder and Vincere are not named among its integrations',
      'AI Interviewer languages are listed as French, English, Spanish, Portuguese and more',
      'The AI opt-out means planning a manual fallback for some candidates',
    ],
    pricing: 'Not published (quote)',
    details: [['Interview format', 'Voice AI, one-way and live video'], ['Free option', 'Not stated']],
    aiDoes: 'Voice AI interviews scored against a rubric; video interviews; assessments',
    agencyFit: 'RPO and staffing solution, built around enterprise clients',
    atsFit: '45+ ATS, including Workday, SuccessFactors and Greenhouse; Bullhorn not named',
    comparisonHref: '/alternatives/hirevue',
    sources: [
      { label: 'HireVue home page', url: 'https://www.hirevue.com/' },
      { label: 'HireVue AI Interviewer', url: 'https://www.hirevue.com/solutions/ai-interviewer' },
      { label: 'HireVue video interviewing', url: 'https://www.hirevue.com/solutions/video-interviewing' },
      { label: 'HireVue for RPO and staffing', url: 'https://www.hirevue.com/solutions/rpo-staffing' },
      { label: 'HireVue integrations', url: 'https://www.hirevue.com/platform/integrations' },
      { label: 'HireVue pricing', url: 'https://www.hirevue.com/pricing' },
    ],
  },
  {
    id: 'hireez',
    name: 'hireEZ',
    website: 'https://hireez.com',
    stage: 'sourcing',
    category: 'Agentic sourcing, screening and outreach',
    bestFor: 'Staffing firms and RPOs that want AI agents to source from the open web and their ATS, pre-screen by phone or chat, and run outreach',
    summary: 'hireEZ is an agentic AI recruiting platform that sits on top of your ATS. Its EZ Agent sources candidates from 45+ platforms and your ATS, ranks and explains inbound applications, runs AI phone or chat pre-screens, schedules interviews and runs outreach campaigns. It has a dedicated offer for staffing firms and RPOs.',
    strengths: [
      'Agentic sourcing across the open web and your own ATS',
      'AI review that ranks and explains inbound applications',
      'AI phone or chat pre-screens with an integrity report',
      'Listed on the Bullhorn Marketplace; 40+ native ATS integrations',
      'A published solo price with a 7-day free trial',
    ],
    watchOuts: [
      'Enterprise pricing is set against the tools it replaces, after a four-week verification',
      'The free trial is described for the solo plan',
      'JobAdder and Vincere are not named among its integrations',
    ],
    pricing: 'Solo from $494/month with a 7-day free trial; Enterprise on request',
    details: [['Interview format', 'AI phone or chat pre-screens'], ['Free option', '7-day free trial (solo plan)']],
    aiDoes: 'Sources passive candidates; ranks applicants; AI phone or chat pre-screens',
    agencyFit: 'Dedicated staffing and RPO solution',
    atsFit: 'Bullhorn Marketplace listing; 40+ native ATS integrations',
    sources: [
      { label: 'hireEZ EZ Agent', url: 'https://hireez.com/ez-agent/' },
      { label: 'hireEZ pricing', url: 'https://hireez.com/pricing/' },
      { label: 'hireEZ for staffing', url: 'https://hireez.com/solutions/staffing/' },
      { label: 'hireEZ AI screening', url: 'https://hireez.com/ai-screening/' },
      { label: 'hireEZ on the Bullhorn Marketplace', url: 'https://www.bullhorn.com/marketplace/hireez/' },
    ],
  },
  {
    id: 'juicebox',
    pricingHref: '/guides/juicebox-pricing',
    name: 'Juicebox',
    website: 'https://juicebox.ai',
    stage: 'sourcing',
    category: 'AI candidate sourcing (PeopleGPT)',
    bestFor: 'Agencies that source passive candidates and want plain-language search, AI ranking and outreach at a published per-seat price',
    summary: 'Juicebox, also called PeopleGPT, lets recruiters search 800M+ profiles from 30+ data sources in plain language, ranks up to 5,000 profiles against their criteria and runs personalised email outreach. Juicebox Agents keep sourcing in the background and learn from feedback. It says 500+ staffing agencies use it.',
    strengths: [
      'Plain-language search across 800M+ profiles, with no Boolean',
      'Ranks up to 5,000 profiles and explains each match',
      'Always-on sourcing agents that learn from recruiter feedback',
      'AI-personalised multi-step email outreach',
      'A free plan, and published prices from $99 per seat a month',
    ],
    watchOuts: [
      'ATS and CRM integration is listed on the custom-priced Business plan',
      'Growth is capped at 5 seats, and contact credits are capped by plan',
      'Two-way ATS sync needs admin permissions in the ATS',
      'It finds candidates; it does not screen or interview applicants',
    ],
    pricing: 'Free; Starter $99 and Growth $179 per seat/month billed yearly ($119 and $199 monthly); Business on request; Agents $199 per agent/month',
    details: [['Interview format', 'None: sourcing only'], ['Free option', 'Free plan with limited searches']],
    aiDoes: 'Plain-language search; ranks and explains profiles; drafts outreach',
    agencyFit: 'Plans for agencies; says 500+ staffing agencies use it',
    atsFit: '41 ATS and 21 CRMs, including JobAdder; Bullhorn integration page',
    sources: [
      { label: 'Juicebox home page', url: 'https://juicebox.ai/' },
      { label: 'Juicebox pricing', url: 'https://juicebox.ai/pricing' },
      { label: 'Juicebox Agents', url: 'https://juicebox.ai/agents' },
      { label: 'Juicebox Bullhorn integration', url: 'https://juicebox.ai/integrations/bullhorn' },
    ],
  },
  {
    id: 'metaview',
    name: 'Metaview',
    website: 'https://www.metaview.ai',
    stage: 'notes',
    category: 'AI interview notes and screening',
    bestFor: 'Agencies and executive search firms that want every client and candidate call written up automatically, with notes and scorecards synced to the ATS',
    summary: 'Metaview’s AI notetaker records live interviews on Zoom, Google Meet, Teams, Webex, by phone or in person, with or without a bot, and writes notes, summaries and scorecards to the ATS. It also sells agents for sourcing, application review and async AI voice screening. It names recruiting agencies and executive search partners among its users.',
    strengths: [
      'Notes from video, phone and in-person interviews, with or without a bot',
      'Auto-filled ATS scorecards and candidate summaries',
      'Async AI voice screening with follow-up questions and rubric scoring',
      'Separate agents for sourcing and application review',
      'Free plans, and Notetaker Pro at $60 per user a month',
    ],
    watchOuts: [
      'The free Notetaker plan keeps only the last 14 days of calls',
      'Prices are shown per user per month without saying how they are billed',
      'Bullhorn is on its integrations list, but the depth of that integration is not described',
      'Vincere is not listed',
    ],
    pricing: 'Notetaker Free or Pro $60/user/month; Sourcing from $100/user/month; Application Review Pro $150/month; full platform on request',
    details: [['Interview format', 'Notes on live calls; async AI voice screens'], ['Free option', 'Free Notetaker plan: 25 calls per user a month']],
    aiDoes: 'Writes interview notes and scorecards; async AI voice screens; sourcing',
    agencyFit: 'Names recruiting agencies and executive search among users',
    atsFit: 'Bullhorn, JobAdder, Loxo and Recruit CRM on its list; Vincere not listed',
    sources: [
      { label: 'Metaview home page', url: 'https://www.metaview.ai/' },
      { label: 'Metaview pricing', url: 'https://www.metaview.ai/pricing' },
      { label: 'Metaview interview notes', url: 'https://www.metaview.ai/interview-notes' },
      { label: 'Metaview screening', url: 'https://www.metaview.ai/screening' },
      { label: 'Metaview integrations', url: 'https://www.metaview.ai/integrations' },
    ],
  },
  {
    id: 'bullhorn',
    pricingHref: '/guides/bullhorn-pricing',
    name: 'Bullhorn',
    website: 'https://www.bullhorn.com',
    stage: 'ats',
    category: 'Agency ATS with AI agents (Amplify)',
    bestFor: 'Staffing and recruitment agencies choosing a system of record that includes AI agents for sourcing, screening and outreach',
    summary: 'Bullhorn’s Amplify AI runs across its staffing ATS and CRM. Amplify Digital Workers source, screen and run outreach around the clock, including structured interviews by chat or voice that are summarised, scored and routed to a recruiter. Search and Match ranks candidates with AI relevancy scoring.',
    strengths: [
      'Digital Workers with 15+ skills, including Screen, Match, Outreach and Verify',
      'Structured screening interviews by chat or voice, scored and routed to a recruiter',
      'Amplify Chat, a staffing language model inside Bullhorn',
      'Search and Match with AI relevancy scoring',
      'A marketplace of 300+ pre-integrated partners',
    ],
    watchOuts: [
      'Most AI features sit in the Pro and Max editions, which are priced on request',
      'Agentic screening is listed in the Max edition',
      'No native video interviewing is named',
    ],
    pricing: 'Starter $99 and Core $165 per user/month (UK £80 and £120); Pro and Max on request',
    details: [['Interview format', 'Chat or voice screening (Digital Workers)'], ['Free option', 'Not stated']],
    aiDoes: 'AI agents source, screen by chat or voice and run outreach inside the ATS',
    agencyFit: 'Built for staffing and recruitment agencies',
    atsFit: 'It is the ATS; 300+ marketplace partners',
    sources: [
      { label: 'Bullhorn Amplify', url: 'https://www.bullhorn.com/products/amplify/' },
      { label: 'Amplify Digital Workers', url: 'https://www.bullhorn.com/products/amplify/digital-workers/' },
      { label: 'Bullhorn pricing', url: 'https://www.bullhorn.com/pricing/' },
    ],
  },
  {
    id: 'loxo',
    pricingHref: '/guides/loxo-pricing',
    name: 'Loxo',
    website: 'https://www.loxo.co',
    stage: 'ats',
    category: 'Agency ATS with sourcing agents',
    bestFor: 'Direct hire and executive search firms that want sourcing and AI agents inside the ATS',
    summary: 'Loxo’s ATS and CRM include sourcing on an 850M-person talent graph and a set of AI agents that take intake notes, write job descriptions, build longlists of up to 10,000 candidates, rank shortlists with evidence and draft submittals. An AI Notetaker writes up calls on Zoom, Google Meet and Teams.',
    strengths: [
      'A Longlist agent that builds lists of up to 10,000 candidates per search',
      'A Shortlist agent that ranks candidates across seven dimensions, with evidence',
      'An AI Notetaker for calls and interviews',
      'Loxo MCP for Claude, ChatGPT and Cursor',
      'A 7-day free trial of Professional',
    ],
    watchOuts: [
      'The full set of agents is on Professional, from $199 per user a month billed annually',
      'Scheduling and outreach agents are marked coming soon',
      'No AI interviewer; one-way video comes through Hireflix',
    ],
    pricing: 'Core from $149 and Professional from $199 per user/month, billed annually; Enterprise custom',
    details: [['Interview format', 'None: AI notes on live calls'], ['Free option', '7-day free trial']],
    aiDoes: 'Agents build longlists, rank shortlists, write job descriptions and submittals; call notes',
    agencyFit: 'Built for direct hire, search, staffing and RPO firms',
    atsFit: 'It is the ATS; marketplace, MCP, and API on Professional',
    sources: [
      { label: 'Loxo AI agents', url: 'https://www.loxo.co/ai-agents' },
      { label: 'Loxo AI Notetaker', url: 'https://www.loxo.co/ai-agents/ai-notetaker' },
      { label: 'Loxo pricing', url: 'https://www.loxo.co/pricing' },
    ],
  },
];

export const CHOOSING_CRITERIA: { title: string; body: string; href?: string; link?: string }[] = [
  { title: 'Which stage costs your desk the most hours?', body: 'Count the hours consultants spend each week reading CVs, phone screening, sourcing and writing up notes. Buy AI for the biggest number first.', href: '/screening-cost-calculator', link: 'Estimate your screening hours' },
  { title: 'Can you see why a candidate scored as they did?', body: 'Open one completed candidate in the trial. If you cannot see the evidence behind a score, you cannot defend the shortlist to a client or a candidate.', href: '/blog/explainable-ai-in-recruiting', link: 'What explainable AI means' },
  { title: 'Is the output ready for a client?', body: 'Agencies sell shortlists. Check whether you can share candidates with a client who has no login, and control what that client sees.' },
  { title: 'Does it work with your ATS?', body: 'Ask whether the integration is native, a marketplace listing or Zapier, and which plan includes it. Bullhorn, JobAdder and Vincere users should check each vendor’s listing.', href: '/integrations', link: 'Beatview integrations' },
  { title: 'Does the price scale with your volume?', body: 'Per seat, per job, per interview or quote only. Model the cost at your real number of live roles, not the three in the demo.' },
  { title: 'What are candidates told about AI?', body: 'Rules differ by place. New York City requires bias audits for automated employment decision tools, and the EU AI Act treats AI used to select candidates as high-risk. Ask each vendor what notice and human review it supports.', href: '/blog/ai-hiring-laws', link: 'AI hiring laws explained' },
];

export const PILOT_STEPS: { title: string; body: string }[] = [
  { title: 'Week 1: pick one live role', body: 'Choose a role with real inbound volume. Write down today’s numbers: hours to a shortlist, candidates screened and first interviews held.' },
  { title: 'Week 2: run it beside your process', body: 'Keep the consultant’s normal review running, and let the tool screen and interview the same applicants.' },
  { title: 'Week 3: compare the shortlists', body: 'Where the tool and the consultant disagree, read the evidence, and note which call you would defend to the client.' },
  { title: 'Week 4: decide on your own numbers', body: 'Hours saved, shortlist overlap, interview completion and client feedback. Keep the tool only if it beats the baseline.' },
];

export const SHORT_ANSWER = 'There is no single best AI recruiting software for an agency, only the best tool for the stage that costs your desk the most hours. To screen and interview every inbound applicant, Beatview scores each application with reasons, runs structured one-way AI video interviews and shares the evidence with clients, starting on a free plan. If you want AI to interview by phone or chat, compare Alex, HeyMilo, Tenzo and Glider AI. For sourcing passive candidates, look at hireEZ or Juicebox; for notes on live calls, Metaview; and if you are choosing an ATS anyway, Bullhorn and Loxo build AI agents into it.';

export const AI_RECRUITING_FAQS = [
  { question: 'What is the best AI recruiting software for recruitment agencies?', answer: 'It depends on the stage you want AI to take over. For screening and interviewing inbound applicants with evidence you can share with clients, Beatview, which has a free plan. For AI interviews by phone or chat, Alex, HeyMilo, Tenzo or Glider AI. For sourcing passive candidates, hireEZ or Juicebox. For notes on live calls, Metaview. If you are choosing an ATS anyway, Bullhorn and Loxo build AI agents into it.' },
  { question: 'What does AI recruiting software do?', answer: 'It takes over parts of hiring work: reading and ranking applications, running first interviews by video, voice or chat, sourcing passive candidates, writing up interview notes, and drafting job ads and outreach. The better tools show the evidence behind each output and leave the decision to a recruiter.' },
  { question: 'Which AI recruiting tools work with Bullhorn?', answer: `As of ${AI_RECRUITING_REVIEWED_LABEL}, Glider AI and hireEZ are listed on the Bullhorn Marketplace. Alex, HeyMilo, Tenzo, Metaview and Juicebox name a Bullhorn integration on their own websites. Bullhorn’s own Amplify AI screens candidates by chat or voice inside Bullhorn. Beatview connects to Bullhorn through Zapier on Enterprise, or through a native integration on request.` },
  { question: 'Is there free AI recruiting software?', answer: 'Some tools have free plans. Beatview’s Free plan covers one active job with unlimited candidates (AI video interviews are on Pro). Juicebox has a free plan with limited searches, and Metaview’s free Notetaker covers 25 calls per user a month. hireEZ offers a 7-day free trial on its solo plan. Alex, HeyMilo, Tenzo, Glider AI and HireVue do not publish free plans.' },
  { question: 'How much does AI recruiting software cost?', answer: `Published prices as of ${AI_RECRUITING_REVIEWED_LABEL}: Beatview Pro is $219 a month, or $179 a month billed annually. Juicebox starts at $99 per seat a month billed yearly, Metaview Notetaker Pro is $60 per user a month, and hireEZ starts at $494 a month for a solo recruiter. Bullhorn starts at $99 per user a month, with most AI in editions priced on request, and Loxo’s agents come with Professional, from $199 per user a month billed annually. Alex, HeyMilo, Tenzo, Glider AI and HireVue quote on request.` },
  { question: 'Will AI recruiting software replace recruiters?', answer: 'No. It takes over the repetitive first pass: reading every application, asking the same first-round questions and writing things up. Recruiters still own the client relationship, the judgement calls and the final decision. In Beatview, the agent screens and interviews, but a recruiter decides who moves forward and who is sent to a client.' },
  { question: 'Is it legal to use AI in recruitment?', answer: 'Yes, with conditions that depend on where you hire. New York City requires a bias audit and candidate notice for automated employment decision tools. The EU AI Act classes AI used to recruit or select candidates as high-risk, with duties for providers and users. In the UK, data protection law gives candidates rights around solely automated decisions. Keep a human decision-maker and tell candidates how they are assessed.' },
] as const;
